/**
 * TrendingTable - one of the two side-by-side Trending tables (by_fp or
 * by_type). Each row leads with an identity cell (market chip + label for
 * FPs, plain label for types) wrapped in a react-router Link, then a single
 * "Trend" rate bar, then the numeric columns (New / Blocked / Notes /
 * Open).
 *
 * CORE RULE: the Trend bar encodes RATE OF CHANGE (delta = window vs prior),
 * not absolute volume. A small FP flaring up ranks as loudly as a big one,
 * because each table scales its own delta against that table's max delta. It
 * is a SINGLE rate bar (window-vs-prior), not a multi-point sparkline - the
 * endpoint only gives us two points.
 */
import React from 'react'
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import type { TrendingRow } from '../../../../services/admin/dataExceptionsService'
import { dqHeatBackground } from '../../../../theme/dqHeatRamp'
import { poolwerxColors } from '../../../../theme/colors'
import MarketChip from './MarketChip'

interface Props {
  rows: TrendingRow[]
  /** Builds the inspector/grid destination for a row's identity cell. */
  linkTo: (row: TrendingRow) => string
  /** Show the market chip in the identity cell (by_fp only). */
  showMarket: boolean
  /** When set, the Notes count becomes a button that drills into that
   * row's note threads. Plain text when omitted or the count is 0. */
  onNotesClick?: (row: TrendingRow) => void
}

const headCellSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  color: poolwerxColors.teal,
  borderBottom: '1px solid',
  borderColor: 'divider',
  py: 1,
}

const bodyCellSx = {
  borderBottom: '0.5px solid',
  borderColor: 'divider',
  py: 0.75,
}

/** Red when worsening (delta>0), green when easing (delta<0), grey when flat. */
const deltaColor = (delta: number): string =>
  delta > 0 ? poolwerxColors.error : delta < 0 ? poolwerxColors.success : poolwerxColors.grey[500]

const deltaLabel = (delta: number): string => (delta > 0 ? `+${delta}` : `${delta}`)

const TrendBar: React.FC<{ delta: number; maxDelta: number }> = ({ delta, maxDelta }) => {
  // Only rising deltas read as a "flare"; eased/flat rows show an empty track.
  const intensity = delta > 0 ? delta : 0
  const widthPct = maxDelta > 0 ? Math.max((intensity / maxDelta) * 100, intensity > 0 ? 8 : 0) : 0
  const fill = dqHeatBackground(intensity, maxDelta)
  return (
    <Box
      sx={{
        width: 90,
        height: 8,
        borderRadius: 1,
        bgcolor: poolwerxColors.grey[100],
        border: '0.5px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: `${widthPct}%`, height: '100%', bgcolor: fill }} />
    </Box>
  )
}

const NumCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums' }}>
    {children}
  </TableCell>
)

const TrendingTable: React.FC<Props> = ({ rows, linkTo, showMarket, onNotesClick }) => {
  const maxDelta = Math.max(...rows.map((r) => (r.delta > 0 ? r.delta : 0)), 1)

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headCellSx}>{showMarket ? 'Franchise' : 'Exception type'}</TableCell>
          <TableCell sx={headCellSx}>Trend</TableCell>
          <TableCell sx={headCellSx} align="right">New</TableCell>
          <TableCell sx={headCellSx} align="right">Blocked</TableCell>
          <TableCell sx={headCellSx} align="right">Notes</TableCell>
          <TableCell sx={headCellSx} align="right">Open</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} hover>
            <TableCell sx={{ ...bodyCellSx, maxWidth: 220 }}>
              <Box
                component={RouterLink}
                to={linkTo(row)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': { color: poolwerxColors.teal },
                }}
              >
                {showMarket && <MarketChip market={row.market} />}
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
                  {row.label}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={bodyCellSx}>
              <TrendBar delta={row.delta} maxDelta={maxDelta} />
            </TableCell>
            <NumCell>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {row.new_window}
              </Box>
              {row.delta !== 0 && (
                <Typography
                  component="sup"
                  sx={{ fontSize: '0.65rem', fontWeight: 700, color: deltaColor(row.delta), ml: 0.25 }}
                >
                  {deltaLabel(row.delta)}
                </Typography>
              )}
            </NumCell>
            <NumCell>{row.new_blocks_window}</NumCell>
            <NumCell>
              {onNotesClick && row.notes_window > 0 ? (
                <Link
                  component="button"
                  type="button"
                  onClick={() => onNotesClick(row)}
                  sx={{
                    fontSize: 'inherit',
                    fontWeight: 600,
                    color: poolwerxColors.teal,
                    textDecorationColor: 'currentColor',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {row.notes_window}
                </Link>
              ) : (
                row.notes_window
              )}
            </NumCell>
            <NumCell>{row.open_total}</NumCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default TrendingTable
